import React from "react";
import {
  Badge,
  Box,
  Button,
  Center,
  HStack,
  Heading,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { useNavigate } from "react-router-dom";

import DriversPage from "./DriversPage";
import WarehouseAssignmentsPage from "./WarehouseAssignmentsPage";
import DispatchQueuePage from "./DispatchQueuePage";
import DriverAssignmentPage from "./DriverAssignmentPage";
import PinManagementPage from "../Pins/PinManagementPage";
import PinVerificationPage from "../Pins/PinVerificationPage";
import {
  useAdminAccess,
  AdminAccessProvider,
} from "../Analytics/shared/AdminAccess/AdminAccessContext";

const DriverModuleHubContent: React.FC = () => {
  const { theme } = useCustomTheme();
  const navigate = useNavigate();
  const { userId, role, isSuperAdmin, loading } = useAdminAccess();

  const tabLabels = [
    "Drivers",
    "Warehouse Assignments",
    "Dispatch Queue · Assign / Unassign",
    "Driver Jobs & Assignments",
    "PIN Driver Assignment",
    "PIN Verification",
  ];

  const panelStyles = {
    w: "100%",
    maxW: "100%",
    minW: 0,
    p: { base: 2, sm: 3, md: 4 },
    borderRadius: { base: "lg", md: "xl" },
    bg: theme.surface,
    border: "1px solid",
    borderColor: theme.border,
    overflowX: "auto" as const,
    sx: {
      WebkitOverflowScrolling: "touch",
      "& > *": {
        maxWidth: "100%",
      },
    },
  };

  if (loading) {
    return (
      <Center minH="320px">
        <Spinner />
      </Center>
    );
  }

  if (!userId) {
    return (
      <Center minH="320px" px={4}>
        <Box textAlign="center">
          <Heading size="md" color={theme.text}>
            Sign in required
          </Heading>
          <Text mt={2} color={theme.textSubtle}>
            Sign in before opening Driver Admin Hub.
          </Text>
        </Box>
      </Center>
    );
  }

  return (
    <Box
      w="100%"
      maxW="100%"
      minW={0}
      px={{ base: 2, sm: 3, md: 6 }}
      py={{ base: 3, md: 6 }}
      overflowX="hidden"
    >
      <Box mb={{ base: 4, md: 5 }} px={{ base: 1, md: 0 }}>
        <Badge
          mb={2}
          borderRadius="full"
          px={3}
          py={1}
          colorScheme={isSuperAdmin ? "purple" : "blue"}
        >
          {isSuperAdmin ? "All platform accounts" : "Current account only"}
        </Badge>

        <Heading
          color={theme.text}
          fontSize={{ base: "xl", sm: "2xl", md: "3xl" }}
          lineHeight="short"
        >
          Driver Admin Hub
        </Heading>

        <Text
          mt={1.5}
          maxW="850px"
          fontSize={{ base: "xs", sm: "sm" }}
          lineHeight="tall"
          color={theme.textSubtle}
        >
          {isSuperAdmin
            ? "Manage drivers, warehouses, dispatch jobs, assignments, and PIN workflows across every admin account."
            : "Manage only the drivers, warehouses, dispatch jobs, assignments, and PIN workflows linked to your account."}
        </Text>

        <Text mt={1} fontSize="xs" color={theme.textMuted}>
          Role: {role || "user"} · UID: {userId}
        </Text>

        <HStack mt={4} spacing={2} flexWrap="wrap">
          <Button
            size="sm"
            colorScheme="blue"
            onClick={() => navigate("/drivers/admin/dispatch")}
          >
            Assign / Unassign Drivers
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate("/drivers/assignments")}
          >
            Driver Jobs & Assignments
          </Button>
        </HStack>
      </Box>

      <Tabs variant="unstyled" width="full" maxW="100%" minW={0} isLazy>
        <Box
          w="100%"
          maxW="100%"
          minW={0}
          overflowX={{ base: "visible", md: "auto" }}
          overflowY="hidden"
          p={{ base: 1.5, md: 0 }}
          bg={theme.surfaceAlt}
          border="1px solid"
          borderColor={theme.border}
          borderRadius={{ base: "xl", md: "md" }}
          sx={{
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "thin",
          }}
        >
          <TabList
            display={{ base: "grid", md: "flex" }}
            gridTemplateColumns={{
              base: "repeat(2, minmax(0, 1fr))",
              sm: "repeat(3, minmax(0, 1fr))",
            }}
            gap={{ base: 1.5, md: 1 }}
            minW={{ base: 0, md: "max-content" }}
            w="100%"
            borderBottomWidth={{ base: "0", md: "1px" }}
            borderColor={theme.border}
          >
            {tabLabels.map((label) => (
              <Tab
                key={label}
                position="relative"
                minW={0}
                minH={{ base: "52px", md: "auto" }}
                px={{ base: 2, sm: 3, md: 5 }}
                py={{ base: 2.5, md: 3 }}
                fontSize={{ base: "xs", sm: "sm", md: "md" }}
                fontWeight="700"
                lineHeight={{ base: "short", md: "normal" }}
                textAlign="center"
                whiteSpace={{ base: "normal", md: "nowrap" }}
                wordBreak="break-word"
                color={theme.textMuted}
                bg="transparent"
                borderRadius={{ base: "lg", md: "md" }}
                borderBottom={{
                  base: "1px solid transparent",
                  md: "2px solid transparent",
                }}
                _hover={{
                  color: theme.text,
                  bg: theme.hover,
                }}
                _selected={{
                  color: theme.text,
                  bg: theme.surface,
                  borderColor: theme.inputBorderFocus,
                  boxShadow: theme.shadowSm,
                }}
                transition="all 0.2s ease"
              >
                {label}
              </Tab>
            ))}
          </TabList>
        </Box>

        <TabPanels mt={{ base: 3, md: 4 }} w="100%" maxW="100%" minW={0}>
          <TabPanel w="100%" maxW="100%" minW={0} px={0} py={0}>
            <Box {...panelStyles}>
              <DriversPage />
            </Box>
          </TabPanel>

          <TabPanel w="100%" maxW="100%" minW={0} px={0} py={0}>
            <Box {...panelStyles}>
              <WarehouseAssignmentsPage />
            </Box>
          </TabPanel>

          <TabPanel w="100%" maxW="100%" minW={0} px={0} py={0}>
            <Box {...panelStyles}>
              <DispatchQueuePage />
            </Box>
          </TabPanel>

          <TabPanel w="100%" maxW="100%" minW={0} px={0} py={0}>
            <Box {...panelStyles}>
              <DriverAssignmentPage />
            </Box>
          </TabPanel>

          <TabPanel w="100%" maxW="100%" minW={0} px={0} py={0}>
            <Box {...panelStyles}>
              <PinManagementPage />
            </Box>
          </TabPanel>

          <TabPanel w="100%" maxW="100%" minW={0} px={0} py={0}>
            <Box {...panelStyles}>
              <PinVerificationPage />
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

const DriverModuleHubPage: React.FC = () => (
  <AdminAccessProvider>
    <DriverModuleHubContent />
  </AdminAccessProvider>
);

export default DriverModuleHubPage;
